import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import {
    FaEdit, FaSave, FaTimes, FaUser, FaEnvelope, FaPhone,
    FaMapMarkerAlt, FaCalendarAlt, FaVenusMars, FaIdCard,
    FaTrash, FaUserCog, FaLock, FaHome, FaCity, FaGlobe, FaInfoCircle,
    FaDownload
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Button } from 'react-bootstrap';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import PhoneNumberInput from "../../../utils/PhoneNumberInput";
import CountryInput from "../../../utils/CountryInput";

// La configuration globale du worker pdf.js est maintenant gérée dans src/index.js

// ── Icône PDF ──────────────────────────────────────────────────────────────
const PDF_ICON_URL = 'https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg';

// Libellés FR des types de document d'identité
const IDENTITY_DOCUMENT_TYPE_LABELS = {
    CNI: "Carte Nationale d'Identité",
    PASSPORT: 'Passeport',
    DRIVING_LICENSE: 'Permis de conduire',
    RESIDENCE_PERMIT: 'Titre de séjour'
};

const getIdentityDocumentTypeLabel = (type) => IDENTITY_DOCUMENT_TYPE_LABELS[type] || type || 'Non spécifié';

// ── Détecte le type de fichier à partir d'une URL ──
const getFileType = (url) => {
    if (!url) return 'unknown';
    const withoutQuery = url.split('?')[0];
    const extension = withoutQuery.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) return 'image';
    if (extension === 'pdf') return 'pdf';
    return 'unknown';
};

// ── Composant de lecture PDF ──
const PdfReader = ({ url }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [pdfError, setPdfError] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(true);
    const [numPages, setNumPages] = useState(0);
    const [pageWidth, setPageWidth] = useState(window.innerWidth - 32);
    const containerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        let objectUrl = null;
        setPdfLoading(true);
        setPdfError(false);
        setBlobUrl(null);
        setNumPages(0);

        const load = async () => {
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const blob = await response.blob();
                objectUrl = URL.createObjectURL(blob);
                if (!cancelled) setBlobUrl(objectUrl);
            } catch (e) {
                console.error('Erreur de lecture du document PDF :', e);
                if (!cancelled) {
                    setPdfError(true);
                    setPdfLoading(false);
                }
            }
        };
        if (url) load();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [url]);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                const width = containerRef.current.clientWidth - 20;
                setPageWidth(Math.max(width, 320));
            }
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);

        if (containerRef.current) {
            const resizeObserver = new ResizeObserver(() => updateWidth());
            resizeObserver.observe(containerRef.current);
            return () => {
                window.removeEventListener('resize', updateWidth);
                resizeObserver.disconnect();
            };
        }

        return () => window.removeEventListener('resize', updateWidth);
    }, [blobUrl]);

    if (pdfError) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '3rem 2rem',
                minHeight: '300px'
            }}>
                <img src={PDF_ICON_URL} alt="PDF" style={{ width: 56, height: 56, marginBottom: 12 }} />
                <h6>Aperçu indisponible</h6>
                <p className="text-muted mb-0">Ce document PDF n'a pas pu être chargé pour lecture.</p>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            style={{
                minHeight: '300px',
                overflow: 'auto',
                background: '#f1f3f5',
                padding: '20px 10px',
                width: '100%'
            }}
        >
            {(pdfLoading || !blobUrl) && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '3rem 0',
                    minHeight: '200px'
                }}>
                    <div className="spinner-border text-primary" role="status" />
                    <p className="text-muted mt-3">Chargement du document…</p>
                </div>
            )}
            {blobUrl && (
                <Document
                    file={blobUrl}
                    onLoadSuccess={({ numPages: n }) => {
                        setNumPages(n);
                        setPdfLoading(false);
                    }}
                    onLoadError={(e) => {
                        console.error('Erreur pdf.js :', e);
                        setPdfError(true);
                        setPdfLoading(false);
                    }}
                    loading={null}
                >
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 16,
                        width: '100%'
                    }}>
                        {Array.from({ length: numPages }, (_, i) => (
                            <div
                                key={i}
                                style={{
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    background: '#fff',
                                    width: '100%',
                                    maxWidth: '100%',
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}
                            >
                                <Page
                                    pageNumber={i + 1}
                                    width={pageWidth > 0 ? pageWidth : 320}
                                    renderTextLayer={true}
                                    renderAnnotationLayer={true}
                                />
                            </div>
                        ))}
                    </div>
                </Document>
            )}
        </div>
    );
};

// ── Affichage d'un fichier (miniature cliquable) ──
const FileDisplay = ({ url, label, onOpenPreview }) => {
    const fileType = getFileType(url);

    if (!url) {
        return (
            <div className="file-display-empty">
                <FaIdCard size={24} className="text-muted" />
                <span className="text-muted">Aucun fichier</span>
            </div>
        );
    }

    return (
        <div className="file-display" onClick={() => onOpenPreview(url, fileType, label)}>
            <div className="file-preview">
                {fileType === 'image' ? (
                    <img src={url} alt={label} className="file-image" />
                ) : fileType === 'pdf' ? (
                    <div className="file-pdf">
                        <img src={PDF_ICON_URL} alt="PDF" className="pdf-icon-img" />
                        <span>PDF</span>
                    </div>
                ) : (
                    <div className="file-unknown">
                        <FaIdCard size={32} />
                        <span>Document</span>
                    </div>
                )}
            </div>
        </div>
    );
};

// Composant DisplayField extrait pour éviter les re-rendus
const DisplayField = memo(({ label, value, icon, name, type = 'text', error, isValid, disabled, onChange, isEditing, placeholder }) => {
    const fieldId = `field-${name}`;

    const getBorderColor = () => {
        if (error) return '2px solid #dc3545';
        if (isValid && isEditing) return '2px solid #28a745';
        return '1px solid #ced4da';
    };

    const commonStyles = {
        width: '100%',
        border: getBorderColor(),
        borderRadius: '8px',
        padding: '8px 12px',
        fontSize: '0.95rem',
        color: '#1a202c',
        backgroundColor: isEditing ? '#fff' : '#f8f9fa',
        transition: 'border-color 0.2s ease',
        cursor: isEditing ? 'text' : 'default'
    };

    const renderInput = () => {
        if (!isEditing) {
            return (
                <div className="field-value" style={{
                    ...commonStyles,
                    minHeight: '38px',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    {name === 'dateOfBirth' ? formatDateForDisplay(value) : (value || 'Non spécifié')}
                </div>
            );
        }

        const inputProps = {
            id: fieldId,
            name: name,
            value: value || '',
            onChange: onChange,
            disabled: disabled,
            style: commonStyles,
            className: 'field-value',
            placeholder: placeholder || `Entrez ${label.toLowerCase()}`
        };

        if (type === 'textarea') {
            return <textarea {...inputProps} rows={3} />;
        }

        if (type === 'date') {
            return <input {...inputProps} type="date" />;
        }

        return <input {...inputProps} type={type} />;
    };

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return 'Non spécifié';
        try {
            return dateString.split(' ')[0];
        } catch {
            return dateString;
        }
    };

    return (
        <div className="display-field mb-3">
            <div className="field-label" style={{
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#2c3e50',
                marginBottom: '0.5rem'
            }}>
                {icon} {label}
            </div>
            {renderInput()}
            {error && isEditing && (
                <div className="invalid-feedback" style={{
                    color: '#dc3545',
                    fontSize: '0.875em',
                    marginTop: '0.25rem'
                }}>
                    {error}
                </div>
            )}
        </div>
    );
});

DisplayField.displayName = 'DisplayField';

const ClientDetailModal = ({ user, onClose, onSave }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedUser, setEditedUser] = useState({ ...user });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    // Aperçu image (lightbox, avec zoom)
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSlides, setLightboxSlides] = useState([]);

    // Aperçu document PDF
    const [docModal, setDocModal] = useState({ open: false, url: '', label: '' });

    // ── Aperçu : routage selon le type de fichier ──
    const handleOpenPreview = useCallback((url, type, label = '') => {
        if (!url) return;
        if (type === 'image') {
            setLightboxSlides([{ src: url }]);
            setLightboxOpen(true);
            return;
        }
        if (type === 'pdf') {
            setDocModal({ open: true, url, label });
        }
    }, []);

    const handleCloseDocModal = useCallback(() => setDocModal({ open: false, url: '', label: '' }), []);
    const handleCloseLightbox = useCallback(() => setLightboxOpen(false), []);

    const handleDownloadFile = useCallback((url, filename) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'document';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);

    const formatDateForDisplay = useCallback((dateString) => {
        if (!dateString) return 'Non spécifié';
        try {
            return dateString.split(' ')[0];
        } catch {
            return dateString;
        }
    }, []);

    const formatDateForBackend = useCallback((dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }

            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');

            return `${day}-${month}-${year} ${hours}:${minutes}`;
        } catch {
            return dateString;
        }
    }, []);

    const validateField = useCallback((name, value) => {
        let error = '';

        switch (name) {
            case 'firstName':
            case 'lastName':
                if (!value?.trim()) error = 'Ce champ est obligatoire';
                break;
            case 'email':
                if (!value?.trim()) {
                    error = 'Ce champ est obligatoire';
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    error = 'Email invalide';
                }
                break;
            case 'phoneNumber':
                if (!value) {
                    error = 'Ce champ est obligatoire';
                }
                break;
            case 'username':
            case 'neighborhood':
            case 'city':
            case 'country':
                if (!value?.trim()) error = 'Ce champ est obligatoire';
                break;
            case 'dateOfBirth':
                if (!value) {
                    error = 'Ce champ est obligatoire';
                } else {
                    const today = new Date();
                    const birthDate = new Date(value.includes('T') ? value : formatDateForBackend(value));
                    if (birthDate > today) {
                        error = 'La date ne peut pas être dans le futur';
                    }
                }
                break;
            case 'gender':
                if (!value) error = 'Ce champ est obligatoire';
                break;
            default:
                break;
        }

        return error;
    }, [formatDateForBackend]);

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;

        setEditedUser(prev => ({
            ...prev,
            [name]: value
        }));

        const error = validateField(name, value);
        setErrors(prev => ({
            ...prev,
            [name]: error
        }));
    }, [validateField]);

    const handlePhoneNumberChange = useCallback((value) => {
        setEditedUser(prev => ({
            ...prev,
            phoneNumber: value
        }));

        setErrors(prev => ({...prev, phoneNumber: null}));
    }, []);

    const handlePhoneValidation = useCallback((error) => {
        setErrors(prev => ({...prev, phoneNumber: error}));
    }, []);

    const handleCountryChange = useCallback((value) => {
        setEditedUser(prev => ({
            ...prev,
            country: value
        }));

        setErrors(prev => ({...prev, country: null}));
    }, []);

    const handleCountryValidation = useCallback((error) => {
        setErrors(prev => ({...prev, country: error}));
    }, []);

    const validateForm = useCallback(() => {
        const newErrors = {};
        let isValid = true;

        Object.keys(editedUser).forEach(field => {
            if (field !== 'profilePictureUrl' && field !== 'additionalInfos' && field !== 'habilitations' && field !== 'useRef' && field !== 'password') {
                const error = validateField(field, editedUser[field]);
                if (error) {
                    newErrors[field] = error;
                    isValid = false;
                }
            }
        });

        setErrors(newErrors);
        return isValid;
    }, [editedUser, validateField]);

    const handleSave = useCallback(async () => {
        if (!validateForm()) {
            const firstErrorField = Object.keys(errors).find(key => errors[key]);
            if (firstErrorField) {
                const element = document.querySelector(`[name="${firstErrorField}"]`);
                if (element) {
                    element.focus();
                } else if (firstErrorField === 'phoneNumber') {
                    document.querySelector('.phone-input-container input')?.focus();
                } else if (firstErrorField === 'country') {
                    document.querySelector('.country-input-container input')?.focus();
                }
            }
            return;
        }

        setSaving(true);

        try {
            const userToSave = {
                firstName: editedUser.firstName,
                lastName: editedUser.lastName,
                email: editedUser.email,
                phoneNumber: editedUser.phoneNumber,
                neighborhood: editedUser.neighborhood,
                city: editedUser.city,
                country: editedUser.country,
                username: editedUser.username,
                additionalInfos: editedUser.additionalInfos,
                dateOfBirth: formatDateForBackend(editedUser.dateOfBirth),
                gender: editedUser.gender,
                lastModifiedBy: 'admin'
            };

            await onSave(editedUser.id, userToSave);
            toast.success('Utilisateur mis à jour avec succès');
            setIsEditing(false);
        } catch (error) {
            console.error('Error saving user:', error);
            toast.error('Erreur lors de la mise à jour de l\'utilisateur');
        } finally {
            setSaving(false);
        }
    }, [editedUser, validateForm, errors, formatDateForBackend, onSave]);

    const handleCancel = useCallback(() => {
        setEditedUser({ ...user });
        setErrors({});
        setIsEditing(false);
    }, [user]);

    return (
        <>
            {/* Modal principal */}
            <div
                className="contract-modal-overlay show"
                style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050, overflowY: 'auto'
                }}
                onClick={onClose}
            >
                <div
                    className="contract-modal-container"
                    style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderRadius: '24px 24px 0 0',
                        maxHeight: '95vh', overflowY: 'auto', animation: 'slideUp 0.4s ease'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header fixe */}
                    <div className="modal-header-fixed" style={{
                        position: 'sticky', top: 0, left: 0, right: 0, zIndex: 100,
                        backgroundColor: 'white', borderBottom: '1px solid #e2e8f0',
                        borderTopLeftRadius: '24px', borderTopRightRadius: '24px', padding: '16px 24px'
                    }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="fw-bold mb-0 text-dark">
                                    <FaUser className="me-2 text-primary" />
                                    {isEditing ? 'Modifier le client' : 'Détails du client'}
                                </h4>
                                <p className="text-muted small mt-1 mb-0">
                                    {isEditing ? 'Modifiez les informations du client' : `Consultez les informations de ${user.firstName} ${user.lastName}`}
                                </p>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                {!isEditing ? (
                                    /*<button
                                        onClick={() => setIsEditing(true)}
                                        className="btn btn-primary rounded-pill px-3 d-flex align-items-center gap-2"
                                        disabled={saving}
                                        style={{ fontSize: '0.9rem' }}
                                    >
                                        <FaEdit /> Modifier
                                    </button>*/
                                    <></>
                                ) : (
                                    <>
                                        <button
                                            onClick={handleSave}
                                            className="btn btn-success rounded-pill px-3 d-flex align-items-center gap-2"
                                            disabled={saving}
                                            style={{ fontSize: '0.9rem' }}
                                        >
                                            {saving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    Enregistrement...
                                                </>
                                            ) : (
                                                <>
                                                    <FaSave /> Enregistrer
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={handleCancel}
                                            className="btn btn-outline-secondary rounded-pill px-3 d-flex align-items-center gap-2"
                                            disabled={saving}
                                            style={{ fontSize: '0.9rem' }}
                                        >
                                            <FaTimes /> Annuler
                                        </button>
                                    </>
                                )}
                                <button
                                    className="btn-close"
                                    onClick={onClose}
                                    disabled={saving}
                                ></button>
                            </div>
                        </div>
                    </div>

                    {/* Contenu principal */}
                    <div className="p-4">
                        {/* Photo de profil */}
                        <div className="text-center mb-4">
                            <img
                                src={user.profilePictureUrl || 'https://yrbhgwbzukfejvcphnwa.supabase.co/storage/v1/object/public/driven-home-services/profile/avatar.jpg'}
                                alt={`${user.firstName} ${user.lastName}`}
                                className="rounded-circle"
                                style={{
                                    width: '120px',
                                    height: '120px',
                                    objectFit: 'cover',
                                    border: '4px solid #28a745',
                                    cursor: user.profilePictureUrl ? 'pointer' : 'default',
                                    transition: 'transform 0.2s ease'
                                }}
                                onClick={() => user.profilePictureUrl && handleOpenPreview(user.profilePictureUrl, 'image', 'Photo de profil')}
                                onError={(e) => {
                                    e.target.src = 'https://yrbhgwbzukfejvcphnwa.supabase.co/storage/v1/object/public/driven-home-services/profile/avatar.jpg';
                                }}
                            />
                        </div>

                        {/* Informations personnelles */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-primary">
                                <FaUser className="me-2" />
                                Informations personnelles
                            </h5>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <DisplayField
                                        label="Prénom"
                                        value={editedUser.firstName}
                                        icon={<FaUser className="text-primary" />}
                                        name="firstName"
                                        error={errors.firstName}
                                        isValid={isEditing && editedUser.firstName && !errors.firstName}
                                        disabled={saving}
                                        onChange={handleInputChange}
                                        isEditing={isEditing}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <DisplayField
                                        label="Nom"
                                        value={editedUser.lastName}
                                        icon={<FaUser className="text-primary" />}
                                        name="lastName"
                                        error={errors.lastName}
                                        isValid={isEditing && editedUser.lastName && !errors.lastName}
                                        disabled={saving}
                                        onChange={handleInputChange}
                                        isEditing={isEditing}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <DisplayField
                                        label="Email"
                                        value={editedUser.email}
                                        icon={<FaEnvelope className="text-primary" />}
                                        name="email"
                                        type="email"
                                        error={errors.email}
                                        isValid={isEditing && editedUser.email && !errors.email}
                                        disabled={saving}
                                        onChange={handleInputChange}
                                        isEditing={isEditing}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <div className="display-field mb-3">
                                        <div className="field-label" style={{
                                            fontSize: '0.85rem',
                                            fontWeight: '600',
                                            color: '#2c3e50',
                                            marginBottom: '0.5rem'
                                        }}>
                                            <FaPhone className="text-primary" /> Téléphone
                                        </div>
                                        {isEditing ? (
                                            <>
                                                <PhoneNumberInput
                                                    value={editedUser.phoneNumber}
                                                    onChange={handlePhoneNumberChange}
                                                    onValidation={handlePhoneValidation}
                                                    disabled={saving}
                                                    defaultCountry="CM"
                                                />
                                                {errors.phoneNumber && (
                                                    <div className="invalid-feedback" style={{
                                                        color: '#dc3545',
                                                        fontSize: '0.875em',
                                                        marginTop: '0.25rem'
                                                    }}>
                                                        {errors.phoneNumber}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="field-value" style={{
                                                width: '100%',
                                                border: '1px solid #ced4da',
                                                borderRadius: '8px',
                                                padding: '8px 12px',
                                                fontSize: '0.95rem',
                                                color: '#1a202c',
                                                backgroundColor: '#f8f9fa',
                                                minHeight: '38px',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}>
                                                {editedUser.phoneNumber || 'Non spécifié'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <DisplayField
                                        label="Date de naissance"
                                        value={editedUser.dateOfBirth}
                                        icon={<FaCalendarAlt className="text-primary" />}
                                        name="dateOfBirth"
                                        type="date"
                                        error={errors.dateOfBirth}
                                        isValid={isEditing && editedUser.dateOfBirth && !errors.dateOfBirth}
                                        disabled={saving}
                                        onChange={handleInputChange}
                                        isEditing={isEditing}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <DisplayField
                                        label="Genre"
                                        value={editedUser.gender}
                                        icon={<FaVenusMars className="text-primary" />}
                                        name="gender"
                                        type="text"
                                        error={errors.gender}
                                        isValid={isEditing && editedUser.gender && !errors.gender}
                                        disabled={saving}
                                        onChange={handleInputChange}
                                        isEditing={isEditing}
                                        placeholder="Masculin/Féminin"
                                    />
                                </div>
                                <div className="col-md-12">
                                    <DisplayField
                                        label="Référence"
                                        value={editedUser.useRef}
                                        icon={<FaIdCard className="text-primary" />}
                                        name="useRef"
                                        type="text"
                                        disabled={true}
                                        isEditing={false}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Informations de connexion */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-success">
                                <FaLock className="me-2" />
                                Informations de connexion
                            </h5>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <DisplayField
                                        label="Nom d'utilisateur"
                                        value={editedUser.username}
                                        icon={<FaUser className="text-success" />}
                                        name="username"
                                        error={errors.username}
                                        isValid={isEditing && editedUser.username && !errors.username}
                                        disabled={saving}
                                        onChange={handleInputChange}
                                        isEditing={isEditing}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Localisation */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-warning">
                                <FaMapMarkerAlt className="me-2" />
                                Localisation
                            </h5>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <DisplayField
                                        label="Quartier"
                                        value={editedUser.neighborhood}
                                        icon={<FaHome className="text-warning" />}
                                        name="neighborhood"
                                        error={errors.neighborhood}
                                        isValid={isEditing && editedUser.neighborhood && !errors.neighborhood}
                                        disabled={saving}
                                        onChange={handleInputChange}
                                        isEditing={isEditing}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <DisplayField
                                        label="Ville"
                                        value={editedUser.city}
                                        icon={<FaCity className="text-warning" />}
                                        name="city"
                                        error={errors.city}
                                        isValid={isEditing && editedUser.city && !errors.city}
                                        disabled={saving}
                                        onChange={handleInputChange}
                                        isEditing={isEditing}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <div className="display-field mb-3">
                                        <div className="field-label" style={{
                                            fontSize: '0.85rem',
                                            fontWeight: '600',
                                            color: '#2c3e50',
                                            marginBottom: '0.5rem'
                                        }}>
                                            <FaGlobe className="text-warning" /> Pays
                                        </div>
                                        {isEditing ? (
                                            <>
                                                <CountryInput
                                                    value={editedUser.country}
                                                    onChange={handleCountryChange}
                                                    onValidation={handleCountryValidation}
                                                    disabled={saving}
                                                    required={true}
                                                />
                                                {errors.country && (
                                                    <div className="invalid-feedback" style={{
                                                        color: '#dc3545',
                                                        fontSize: '0.875em',
                                                        marginTop: '0.25rem'
                                                    }}>
                                                        {errors.country}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="field-value" style={{
                                                width: '100%',
                                                border: '1px solid #ced4da',
                                                borderRadius: '8px',
                                                padding: '8px 12px',
                                                fontSize: '0.95rem',
                                                color: '#1a202c',
                                                backgroundColor: '#f8f9fa',
                                                minHeight: '38px',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}>
                                                {editedUser.country || 'Non spécifié'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Document d'identification */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-info">
                                <FaIdCard className="me-2" />
                                Document d'identification
                            </h5>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="display-field mb-3">
                                        <div className="field-label" style={{
                                            fontSize: '0.85rem',
                                            fontWeight: '600',
                                            color: '#2c3e50',
                                            marginBottom: '0.5rem'
                                        }}>
                                            <FaIdCard className="text-info" /> Type de document
                                        </div>
                                        <div className="field-value" style={{
                                            width: '100%',
                                            border: '1px solid #ced4da',
                                            borderRadius: '8px',
                                            padding: '8px 12px',
                                            fontSize: '0.95rem',
                                            color: '#1a202c',
                                            backgroundColor: '#f8f9fa',
                                            minHeight: '38px',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            {getIdentityDocumentTypeLabel(user.identityDocumentType)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mb-2">
                                <div className="field-label" style={{
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    color: '#2c3e50',
                                    marginBottom: '0.5rem'
                                }}>
                                    Fichier soumis
                                </div>
                                <FileDisplay
                                    url={user.identityDocumentUrl}
                                    label={getIdentityDocumentTypeLabel(user.identityDocumentType)}
                                    onOpenPreview={handleOpenPreview}
                                />
                            </div>
                        </div>

                        {/* Informations supplémentaires */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-danger">
                                <FaInfoCircle className="me-2" />
                                Informations supplémentaires
                            </h5>
                            <div className="row g-3">
                                <div className="col-12">
                                    <DisplayField
                                        label="Informations supplémentaires"
                                        value={editedUser.additionalInfos}
                                        icon={<FaInfoCircle className="text-danger" />}
                                        name="additionalInfos"
                                        type="textarea"
                                        disabled={saving}
                                        onChange={handleInputChange}
                                        isEditing={isEditing}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Lightbox pour zoomer les images */}
            <Lightbox
                open={lightboxOpen}
                close={handleCloseLightbox}
                slides={lightboxSlides}
                plugins={[Zoom]}
                zoom={{ maxZoomPixelRatio: 3 }}
                controller={{ closeOnBackdropClick: true }}
            />

            {/* Modal de lecture des documents PDF */}
            {docModal.open && (
                <div className="preview-modal-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.85)',
                    zIndex: 1070,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center'
                }} onClick={handleCloseDocModal}>
                    <div className="preview-modal-container" style={{
                        backgroundColor: 'white',
                        width: '100%',
                        maxWidth: '100%',
                        height: '92vh',
                        maxHeight: '92vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        animation: 'slideUp 0.3s ease'
                    }} onClick={(e) => e.stopPropagation()}>
                        <div className="p-3 border-bottom d-flex justify-content-between align-items-center" style={{
                            backgroundColor: 'white',
                            flexShrink: 0
                        }}>
                            <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                <img src={PDF_ICON_URL} alt="PDF" style={{ width: 22, height: 22 }} />
                                {docModal.label || 'Document PDF'}
                            </h6>
                            <div className="d-flex gap-2">
                                {/*<Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleDownloadFile(docModal.url, (docModal.label || 'document') + '.pdf')}
                                    className="rounded-pill"
                                >
                                    <FaDownload className="me-1" /> Télécharger
                                </Button>*/}
                                <button className="btn-close" onClick={handleCloseDocModal}></button>
                            </div>
                        </div>

                        <div style={{
                            overflow: 'auto',
                            flex: 1,
                            backgroundColor: '#f1f3f5',
                            padding: '0'
                        }}>
                            <PdfReader url={docModal.url} />
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .contract-modal-overlay {
                    animation: fadeIn 0.3s ease;
                }

                .modal-header-fixed {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                ::-webkit-scrollbar {
                    width: 6px;
                }

                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }

                ::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 3px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }

                .field-value:focus {
                    outline: none;
                    border-color: #0d6efd !important;
                    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
                }

                .field-value:disabled {
                    background-color: #e9ecef;
                    cursor: not-allowed;
                }

                .btn-close:focus {
                    box-shadow: none;
                }

                /* Styles pour les fichiers (ajoutés) */
                .file-display {
                    cursor: pointer;
                    display: inline-block;
                }

                .file-image {
                    width: 100px;
                    height: 100px;
                    object-fit: cover;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    transition: transform 0.2s ease;
                }

                .file-image:hover {
                    transform: scale(1.05);
                }

                .file-pdf {
                    width: 100px;
                    height: 100px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    background: #f8f9fa;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                }

                .pdf-icon-img {
                    width: 50px;
                    height: 50px;
                }

                .file-display-empty {
                    width: 100px;
                    height: 100px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    background: #f8f9fa;
                    border-radius: 8px;
                    border: 1px dashed #dee2e6;
                }
            `}</style>
        </>
    );
};

export default ClientDetailModal;